import React, { useState } from 'react';
import CollectionSearch from '../search/CollectionSearch';
import { filterRecords } from '../../utils/search';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  InputAdornment,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Lock as LockIcon, Warning as WarningIcon } from '@mui/icons-material';

function AllocationAdjuster({
  scenarioResult,
  allocationOverrides,
  setAllocationOverrides,
  calculating,
}) {
  const [search, setSearch] = useState('');
  const handleOverrideChange = (categoryId, value) => {
    const numValue = Number(value);
    if (isNaN(numValue)) return;

    setAllocationOverrides(prev => ({
      ...prev,
      [categoryId]: numValue
    }));
  };

  const clearOverride = (categoryId) => {
    setAllocationOverrides(prev => {
      const updated = { ...prev };
      delete updated[categoryId];
      return updated;
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (calculating) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (!scenarioResult) {
    return (
      <Alert severity="info">
        Calculating scenario... Please ensure you have entered baseline income.
      </Alert>
    );
  }

  const { allocations, delta } = scenarioResult;
  const unallocatedDifference = allocations?.unallocated_difference ?? 0;
  const isBalanced = allocations?.is_balanced ?? true;
  const visible = rows => filterRecords(rows || [], search, category => [category.account_name, category.category_name], { rank: true });
  const totalCount = (allocations?.locked?.length || 0) + (allocations?.flexible?.length || 0);
  const shownCount = visible(allocations?.locked).length + visible(allocations?.flexible).length;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Adjust Allocations
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The system has proportionally adjusted your flexible categories. Override any values to customize where the impact lands.
      </Typography>

      {/* Balance Indicator */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          bgcolor: isBalanced ? 'success.light' : 'warning.light',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        {!isBalanced && <WarningIcon color="warning" />}
        <Typography variant="h6">
          Unallocated Difference: {formatCurrency(unallocatedDifference)}
          {isBalanced ? ' (Balanced)' : ''}
        </Typography>
      </Paper>

      {/* Delta Summary */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Chip
          label={`State Change: ${formatCurrency(delta?.from_state_change || 0)}`}
          color={delta?.from_state_change >= 0 ? 'success' : 'error'}
          variant="outlined"
        />
        <Chip
          label={`Factors: ${formatCurrency(delta?.from_factors || 0)}`}
          color={delta?.from_factors >= 0 ? 'success' : 'error'}
          variant="outlined"
        />
        <Chip
          label={`Total Impact: ${formatCurrency(delta?.total || 0)}`}
          color={delta?.total >= 0 ? 'success' : 'error'}
        />
      </Box>

      {/* Show category breakdown only if NOT using calculated fallback */}
      {!allocations?.using_calculated_fallback && <CollectionSearch label="Search allocation adjustments" value={search} onChange={setSearch}
        total={totalCount} shown={shownCount} />}
      {search && !shownCount && <Typography sx={{ mb: 2 }}>No matching allocations.</Typography>}
      {!allocations?.using_calculated_fallback ? (
        <>
          {/* Locked Categories */}
          {allocations?.locked?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LockIcon fontSize="small" /> Locked Categories (No Change)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Account</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visible(allocations.locked).map(cat => (
                      <TableRow key={cat.category_id}>
                        <TableCell>{cat.account_name}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {cat.category_name}
                            <LockIcon fontSize="small" color="action" />
                          </Box>
                        </TableCell>
                        <TableCell align="right">{formatCurrency(cat.current_allocation)}/mo</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Flexible Categories */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Flexible Categories (Adjustable)
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Account</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Current</TableCell>
                    <TableCell align="right">Adjustment</TableCell>
                    <TableCell align="right">Scenario</TableCell>
                    <TableCell align="right" sx={{ minWidth: 150 }}>Override</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible(allocations?.flexible).map(cat => {
                    const hasOverride = allocationOverrides.hasOwnProperty(cat.category_id);
                    const adjustment = cat.adjustment || 0;

                    return (
                      <TableRow
                        key={cat.category_id}
                        sx={{
                          bgcolor: hasOverride ? 'action.selected' : 'inherit',
                        }}
                      >
                        <TableCell>{cat.account_name}</TableCell>
                        <TableCell>{cat.category_name}</TableCell>
                        <TableCell align="right">{formatCurrency(cat.current_allocation)}</TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: adjustment >= 0 ? 'success.main' : 'error.main' }}
                        >
                          {adjustment >= 0 ? '+' : ''}{formatCurrency(adjustment)}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(cat.scenario_allocation)}</TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            value={hasOverride ? allocationOverrides[cat.category_id] : ''}
                            onChange={(e) => handleOverrideChange(cat.category_id, e.target.value)}
                            onBlur={(e) => {
                              if (e.target.value === '') {
                                clearOverride(cat.category_id);
                              }
                            }}
                            placeholder="Auto"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                            sx={{ width: 120 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          No split templates defined. The totals below are based on calculated take-home income.
          To see detailed category breakdowns, set up split templates to define your allocations.
        </Alert>
      )}

      {/* Totals */}
      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>Summary</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Current Total</Typography>
            <Typography variant="h6">{formatCurrency(allocations?.totals?.current_total)}/mo</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Scenario Total</Typography>
            <Typography variant="h6">{formatCurrency(allocations?.totals?.scenario_total)}/mo</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Impact</Typography>
            <Typography
              variant="h6"
              sx={{ color: delta?.total >= 0 ? 'success.main' : 'error.main' }}
            >
              {delta?.total >= 0 ? '+' : ''}{formatCurrency(delta?.total)}/mo
            </Typography>
          </Box>
          {!allocations?.using_calculated_fallback && (
            <>
              <Box>
                <Typography variant="body2" color="text.secondary">Locked</Typography>
                <Typography variant="h6">{formatCurrency(allocations?.totals?.scenario_locked)}/mo</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Flexible</Typography>
                <Typography variant="h6">{formatCurrency(allocations?.totals?.scenario_flexible)}/mo</Typography>
              </Box>
            </>
          )}
        </Box>
      </Paper>

      {!isBalanced && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          The allocations don't balance. Adjust the override values until the unallocated difference is $0.
        </Alert>
      )}
    </Box>
  );
}

export default AllocationAdjuster;
